<template>
  <div id="app" :class="$permissions['content-only'] && '-permission-content-only'">
    <icons />
    <router-view :loading="appGlobalLoading" :error="appGlobalError" />
  </div>
</template>

<script>
import Icons from './components/shared/Icons.vue';
import TimerMixin from './mixins/timer.js';
import SessionMixin from './mixins/session.js';
import ImpulseMixin from './mixins/impulse.js';
import NotificationsMixin from './mixins/notifications';
import HealthCheckMixin from '@/core/shared/vuex-modules/health/HealthCheckMixin';
import SafetyModeMixin from './mixins/safetyMode';
import EntitiesHooksMixin from './mixins/entitiesHooks';
import { get } from 'lodash-es';
import moment from 'moment';
import { InteropNotDefinedError } from '@/core/errors';

export default {
  name: 'AppLauncher',
  components: {
    Icons,
  },
  mixins: [
    TimerMixin,
    SessionMixin,
    NotificationsMixin,
    HealthCheckMixin,
    SafetyModeMixin,
    EntitiesHooksMixin,
    ImpulseMixin,
  ],
  created () {
    this.$store.commit('startLoading', 'initApp');
    // init API with locale from station so we have to fetch station before configuring api
    (async function initApp () {
      try {
        await this.$store.dispatch('getUserFromToken');
        await this.$store.dispatch('getStationIdentity');
        this.$store.dispatch('initApp');
      } catch (e) {
        // DEPRECATION backward compat with <=4.2.15
        if (e instanceof InteropNotDefinedError) {
          if (this.$store.state.launcher.hasReceivedStationIdentity) {
            this.$store.dispatch('initApp');
          } else {
            const unsub = this.$store.watch((state) => state.launcher.hasReceivedStationIdentity, (val) => {
              if (val) {
                unsub();
                this.$store.dispatch('initApp');
              }
            });
          }
        } else {
          throw e;
        }
      }
    }.bind(this))();

    const bookingSubscription = this.$store.watch((state, getters) => getters.currentBookingId, (current, oldId) => {
      this.$store.dispatch('interop/bookingChanged', { id: current });
      if (oldId !== null && oldId !== undefined) {
        this.$store.commit('registerPreviousBooking', { id: oldId });
      }
    });
    const computeCurrentBookingTimeSubscription = this.$store.watch((state, getters) => getters.currentTime, (current, old) => {
      const currentTime = current;
      const state = this.$store.state.launcher;
      const currentBookingTime = state.bookingTimes &&
        state.bookingTimes.find(({ startedAt, endTime, pausedAt }) => {
          if (!startedAt || !endTime) return;
          const now = moment.utc(currentTime);
          return now.isAfter(startedAt) && (pausedAt || now.isBefore(endTime));
        });
      const id = (currentBookingTime && currentBookingTime.id) || null;
      if (state.currentBookingTimeId !== id) this.$store.commit('setCurrentBookingTimeId', id);
    });
    const timerSubscription = this.$store.watch((state, getters) => getters.timerStatus, (current, old) => {
      const activeTimerStates = ['active', 'paused', 'infinite', 'offline'];
      const expiredTimerStates = ['expired', 'disabled', null];
      if (activeTimerStates.includes(old) && expiredTimerStates.includes(current)) {
        this.$store.dispatch('onTimeUp');
      } else if (activeTimerStates.includes(current) && expiredTimerStates.includes(old)) {
        this.$store.dispatch('onTimeStart');
      } else if (old === null) {
        this.$store.dispatch('onTimeInfoAvailable');
      }
    });

    this._storeSubscriptions = [bookingSubscription, computeCurrentBookingTimeSubscription, timerSubscription];
  },
  beforeDestroy () {
    if (this._storeSubscriptions) this._storeSubscriptions.forEach(unsub => unsub());
  },
  computed: {
    appGlobalLoading () {
      return !this.$store.getters.safetyMode ? this.$store.state.launcher.loadings.initApp : false;
    },
    appGlobalError () {
      if (!this.$store.state.launcher.isAppInitialized) return undefined;
      if (this.$store.state.launcher.errors.safetyModeWithoutPersistedState) return 'no-safety-state';
      else if (this.$store.getters.safetyMode && !this.$store.getters.currentStation._lastSyncedAt) return 'network';
      else if (this.$store.state.launcher.errors.initApp) return 'backend';
      else if (!get(this.$store, 'getters.currentStation.organization.isActive')) return 'disabled-organization';
      return undefined;
    },
  },
};
</script>

<style>
@import 'styles/tachyons.css';
@import 'styles/unstyled.css';
@import 'styles/reset.css';
@import 'styles/content.css';
@import 'styles/atomic.css';
@import 'styles/flex.css';

#app {
  font-family: 'Open Sans', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 700px;
  height: 100vh;
}

.wf-loading {
  display: none;
}
</style>
